import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../lib/firebase';
import { doc, getDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

function AppBuilder() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [content, setContent] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState('');

  // Initialize functions once
  const functions = getFunctions();

  useEffect(() => {
    loadApp();
  }, [appId]);

  const loadApp = async () => {
    try {
      const appDoc = await getDoc(doc(db, 'apps', appId));
      if (appDoc.exists()) {
        const appData = { id: appDoc.id, ...appDoc.data() };
        setApp(appData);
        
        // If content exists, show it, otherwise generate
        if (appData.content) {
          setContent(appData.content);
        } else {
          await generateWithAI(appData.description);
        }
      } else {
        setError('App not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error loading app:', error);
      setError('Failed to load app');
    }
    setLoading(false);
  };

  const detectAppType = (prompt) => {
    const lower = prompt.toLowerCase();
    if (lower.includes('game')) return 'game';
    if (lower.includes('blog')) return 'blog';
    if (lower.includes('store') || lower.includes('shop')) return 'e-commerce';
    if (lower.includes('dashboard')) return 'dashboard';
    if (lower.includes('portfolio')) return 'portfolio';
    if (lower.includes('landing')) return 'landing page';
    if (lower.includes('todo') || lower.includes('task')) return 'productivity app';
    if (lower.includes('calculator')) return 'calculator app';
    if (lower.includes('weather')) return 'weather app';
    if (lower.includes('chat') || lower.includes('message')) return 'chat app';
    return 'web app';
  };

  const generateWithAI = async (prompt) => {
  setGenerating(true);
  setError('');
  
  try {
    // Debug: Check auth
    const user = auth.currentUser;
    console.log('Auth check:', {
      isLoggedIn: !!user,
      userId: user?.uid,
      email: user?.email
    });
    
    // Debug: Check function call
    console.log('Calling generateAppContent with:', {
      prompt,
      appId,
      appType: detectAppType(prompt)
    });
    
    const generateAppContent = httpsCallable(functions, 'generateAppContent');
    const result = await generateAppContent({
      prompt: prompt,
      appId: appId,
      appType: detectAppType(prompt)
    });
    
    console.log('Success:', result.data);
    
    if (result.data && result.data.success) {
      setContent(result.data.content);
      setApp(prev => ({ ...prev, content: result.data.content }));
    } else {
      throw new Error('Generation failed');
    }
  } catch (error) {
    console.error('Function call failed:');
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', error);
    
    // Check specific error types
    if (error.code === 'functions/internal') {
      console.log('This is likely the CORS error masking the real issue');
    }
    
    // Better error messages
    if (error.code === 'unauthenticated') {
      setError('You must be logged in to generate content');
      navigate('/login');
    } else if (error.message?.includes('Rate limit')) {
      setError('Too many requests. Please try again in a few minutes.');
    } else {
      setError('Failed to generate content. Using template instead.');
      // Fallback to template
      const template = generateTemplateFromPrompt(prompt);
      setContent(template);
      
      // Save template content
      try {
        await updateDoc(doc(db, 'apps', appId), {
          content: template,
          lastGenerated: new Date(),
          aiGenerated: false
        });
      } catch (saveError) {
        console.error('Error saving template:', saveError);
      }
    }
  }
  setGenerating(false);
};

  const deployApp = async () => {
    if (!content) {
      setError('No content to deploy');
      return;
    }
    
    setDeploying(true);
    setError('');
    
    try {
      // Use the Firebase callable function for deployment
      const deployAppFunction = httpsCallable(functions, 'deployApp');
      
      const result = await deployAppFunction({
        appId: appId,
        content: content
      });
      
      if (result.data && result.data.success) {
        // Update local state
        setApp(prev => ({ 
          ...prev, 
          status: 'live',
          url: result.data.url,
          version: result.data.version
        }));
        
        alert(`App deployed successfully! View it at: ${result.data.url}`);
        
        // Optionally open in new tab
        window.open(result.data.url, '_blank');
      }
    } catch (error) {
      console.error('Deployment error:', error);
      
      if (error.code === 'unauthenticated') {
        setError('You must be logged in to deploy');
        navigate('/login');
      } else if (error.message?.includes('too large')) {
        setError('App content is too large. Please simplify your app.');
      } else {
        setError(error.message || 'Failed to deploy app');
      }
    }
    setDeploying(false);
  };

  const generateTemplateFromPrompt = (prompt) => {
    const lowerPrompt = prompt.toLowerCase();
    
    // Detect type and generate appropriate template
    if (lowerPrompt.includes('blog')) {
      return generateBlogTemplate(prompt);
    } else if (lowerPrompt.includes('store') || lowerPrompt.includes('shop')) {
      return generateStoreTemplate(prompt);
    } else if (lowerPrompt.includes('portfolio')) {
      return generatePortfolioTemplate(prompt);
    } else if (lowerPrompt.includes('todo') || lowerPrompt.includes('task')) {
      return generateTodoTemplate(prompt);
    } else {
      return generateDefaultTemplate(prompt);
    }
  };

  const generateBlogTemplate = (prompt) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My Blog</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            background: #f4f4f4;
        }
        header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            text-align: center;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        header h1 {
            font-size: 2.5rem;
            margin-bottom: 0.5rem;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
        }
        article {
            background: white;
            padding: 2rem;
            margin-bottom: 2rem;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            transition: transform 0.3s ease;
        }
        article:hover {
            transform: translateY(-5px);
        }
        article h2 {
            color: #667eea;
            margin-bottom: 1rem;
        }
        .meta {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 1rem;
        }
        button {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 0.5rem 1.5rem;
            border-radius: 5px;
            cursor: pointer;
            transition: opacity 0.3s ease;
        }
        button:hover {
            opacity: 0.9;
        }
    </style>
</head>
<body>
    <header>
        <h1>My Amazing Blog</h1>
        <p>${prompt}</p>
    </header>
    
    <div class="container">
        <article>
            <h2>Welcome to My Blog!</h2>
            <p class="meta">Published on ${new Date().toLocaleDateString()}</p>
            <p>This is your first blog post. Start sharing your thoughts and ideas with the world!</p>
            <button onclick="alert('Edit this post!')">Read More</button>
        </article>
        
        <article>
            <h2>Getting Started</h2>
            <p class="meta">Published on ${new Date().toLocaleDateString()}</p>
            <p>Customize this template to match your style. Add new posts, change colors, and make it yours!</p>
            <button onclick="alert('Edit this post!')">Read More</button>
        </article>
    </div>

    <script>
        console.log('Blog loaded successfully!');
    </script>
</body>
</html>`;
  };

  const generateTodoTemplate = (prompt) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Todo App</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 2rem;
        }
        .container {
            max-width: 500px;
            margin: 0 auto;
            background: white;
            padding: 2rem;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        h1 {
            color: #667eea;
            margin-bottom: 1rem;
            text-align: center;
        }
        .add-todo {
            display: flex;
            margin-bottom: 2rem;
        }
        input {
            flex: 1;
            padding: 0.8rem;
            border: 2px solid #e0e0e0;
            border-radius: 5px;
            font-size: 1rem;
        }
        button {
            background: #667eea;
            color: white;
            border: none;
            padding: 0.8rem 1.5rem;
            margin-left: 0.5rem;
            border-radius: 5px;
            cursor: pointer;
        }
        .todo-item {
            padding: 1rem;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .todo-item.completed {
            opacity: 0.6;
            text-decoration: line-through;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>My Todo List</h1>
        <div class="add-todo">
            <input type="text" id="todoInput" placeholder="Add a new task...">
            <button onclick="addTodo()">Add</button>
        </div>
        <div id="todoList"></div>
    </div>

    <script>
        let todos = [];
        
        function addTodo() {
            const input = document.getElementById('todoInput');
            if (input.value.trim()) {
                todos.push({ text: input.value, completed: false });
                input.value = '';
                renderTodos();
            }
        }
        
        function toggleTodo(index) {
            todos[index].completed = !todos[index].completed;
            renderTodos();
        }
        
        function deleteTodo(index) {
            todos.splice(index, 1);
            renderTodos();
        }
        
        function renderTodos() {
            const list = document.getElementById('todoList');
            list.innerHTML = todos.map((todo, i) => 
                '<div class="todo-item ' + (todo.completed ? 'completed' : '') + '">' +
                '<span onclick="toggleTodo(' + i + ')">' + todo.text + '</span>' +
                '<button onclick="deleteTodo(' + i + ')">Delete</button>' +
                '</div>'
            ).join('');
        }
        
        document.getElementById('todoInput').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') addTodo();
        });
    </script>
</body>
</html>`;
  };

  const generateDefaultTemplate = (prompt) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${prompt.split(' ').slice(0, 3).join(' ')}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }
        .container {
            text-align: center;
            padding: 2rem;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        h1 {
            font-size: 3rem;
            margin-bottom: 1rem;
        }
        p {
            font-size: 1.2rem;
            margin-bottom: 2rem;
            opacity: 0.9;
        }
        button {
            background: white;
            color: #667eea;
            border: none;
            padding: 1rem 2rem;
            font-size: 1rem;
            border-radius: 50px;
            cursor: pointer;
            transition: transform 0.3s ease;
        }
        button:hover {
            transform: scale(1.05);
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Your App Is Ready!</h1>
        <p>${prompt}</p>
        <button onclick="alert('Customize me!')">Get Started</button>
    </div>
</body>
</html>`;
  };

  const generateStoreTemplate = (prompt) => {
    return generateDefaultTemplate(prompt);
  };

  const generatePortfolioTemplate = (prompt) => {
    return generateDefaultTemplate(prompt);
  };

  if (loading) return <div className="auth-loading"><div className="spinner"></div></div>;
  if (!app) return <div>App not found</div>;

  return (
    <div className="builder-container">
      <nav className="builder-nav">
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          ← Back
        </button>
        <h2>{app.name}</h2>
        <div className="builder-actions">
          <button 
            onClick={() => generateWithAI(app.description)} 
            disabled={generating}
            className="regenerate-btn"
          >
            {generating ? '⚡ Generating...' : '✨ Regenerate'}
          </button>
          <button 
            onClick={() => setShowCode(!showCode)} 
            className="code-toggle-btn"
          >
            {showCode ? '👁️ Preview' : '</> Code'}
          </button>
          <button 
            onClick={deployApp} 
            disabled={deploying || !content}
            className="deploy-btn"
          >
            {deploying ? '🚀 Deploying...' : '🚀 Deploy to Web'}
          </button>
        </div>
      </nav>

      <div className="builder-split-view">
        <div className="prompt-section">
          <h3>Your Request</h3>
          <p>{app.description}</p>
          {error && <p className="error-message">{error}</p>}
          {app?.status === 'live' && app?.url && (
            <div className="deployed-info">
              <p className="success-message">
                ✅ Your app is live at: <a href={app.url} target="_blank" rel="noopener noreferrer">{app.url}</a>
              </p>
            </div>
          )}
        </div>

        <div className="preview-section">
          {generating ? (
            <div className="generating-animation">
              <div className="generating-spinner"></div>
              <p>Creating your app with AI magic...</p>
            </div>
          ) : showCode ? (
            <div className="code-view">
              <div className="code-header">
                <span>HTML/CSS/JS</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(content)}
                  className="copy-btn"
                >
                  📋 Copy
                </button>
              </div>
              <pre className="code-content">
                <code>{content}</code>
              </pre>
            </div>
          ) : (
            <iframe
              srcDoc={content}
              title="App Preview"
              className="preview-iframe"
              sandbox="allow-scripts allow-same-origin allow-modals"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AppBuilder;